import { redirect } from '@/lib/oauth/redirect'
import { isStandalone, redirectUri } from '@/params'
import type { Message } from '@/types/message'
import { satisfies } from 'compare-versions'
import { z } from 'zod'

const LegacySigninResultMessage = z.object({
  type: z.literal('legacy_signin_result'),
  payload: z.object({
    success: z.boolean(),
  }),
})

export function signInWithLegacy(email: string, appVersion: string) {
  // Always use a redirect flow when the page is opened in "standalone" mode –
  // this mimics the behaviour of the native web-view where messages cannot be
  // exchanged with the parent context.
  if (isStandalone) {
    redirect(redirectUri, { sign_in_with_legacy: email })
    return Promise.resolve(true)
  }

  // For modern app versions (≥ 4.94.0) we can rely on the native bridge to
  // respond with a `legacy_signin_result` message.
  if (satisfies(appVersion, '>=4.94.0')) {
    return new Promise<boolean>((resolve) => {
      const handler = (event: MessageEvent) => {
        const parsed = LegacySigninResultMessage.safeParse(event.data)
        if (!parsed.success) return

        resolve(parsed.data.payload.success)
        window.removeEventListener('message', handler)
      }

      window.addEventListener('message', handler)

      window.postMessage({
        type: 'sign_in_with_legacy',
        payload: { email },
      } satisfies Message)
    })
  }

  // Legacy versions (< 4.94.0) fall back to a fire-and-forget message.
  return new Promise<boolean>((resolve) => {
    window.postMessage({
      type: 'sign_in_with_legacy',
      payload: { email },
    } satisfies Message)

    resolve(true)
  })
}
